import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {TableauViz, TableauDialogType, TableauEventType} from '@tableau/embedding-api';
import {
  Alert,
  Button,
  Menu,
  MenuItem,
  Position,
  Dialog,
  InputGroup,
  FormGroup,
  Classes,
  Intent,
  Toaster,
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Popover2} from '@blueprintjs/popover2';

import {Loading} from '../components';

interface EmbedWidgetProps {
  loading: boolean;
  embedUrl: string;
  filters: string;
  onChange?: (value: any) => void;
}

const AppToaster = Toaster.create({
  position: Position.TOP,
});

const EmbedWidget: FunctionComponent<EmbedWidgetProps> = ({
  loading,
  embedUrl,
  filters,
  onChange,
}: EmbedWidgetProps) => {
  const vizRef = useRef<HTMLDivElement | null>(null);

  const [viz, setViz] = useState<TableauViz | null>(null);
  const [customViews, setCustomViews] = useState<any[]>([]);
  const [hasCustomViews, setHasCustomViews] = useState<boolean>(false);
  const [showManageViewsDialog, setShowManageViewsDialog] = useState<boolean>(false);
  const [tableauIsAvailable, setTableauIsAvailable] = useState<boolean>(false);
  const [saveCustomViewDialogOpen, setSaveCustomViewDialogOpen] = useState<boolean>(false);
  const [newCustomViewName, setNewCustomViewName] = useState<string>('');
  const [savedCustomViewName, setSavedCustomViewName] = useState<string>('');
  const [loadingCustomViews, setLoadingCustomViews] = useState<boolean>(false);

  // Confirmation dialog for setting as default
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>('');

  // Confirmation dialog for deleting custom view
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState<string>('');
  const [deleteViewName, setDeleteViewName] = useState<string>('');

  const loadCustomViewList = async (vizInstance: TableauViz | null) => {
    if (!vizInstance) {
      return;
    }
    setLoadingCustomViews(true);
    try {
      const customViews = await vizInstance.workbook.getCustomViewsAsync();
      if (customViews.length > 0) {
        const customViewsData = customViews.map((customView: any) => ({
          name: customView.name,
          isDefault: customView.default,
          isSelected: customView.isActive,
        }));

        const hasCustomViewAsDefault = customViewsData.some((v: any) => v.isDefault);
        const originalView = {
          name: 'Original',
          isDefault: !hasCustomViewAsDefault,
          isSelected: !customViewsData.some((v: any) => v.isSelected),
        };

        setCustomViews([originalView, ...customViewsData]);
        setHasCustomViews(true);
      } else {
        setCustomViews([{name: 'No saved views', isDefault: false, isSelected: false}]);
      }
    } catch (error) {
      AppToaster.show({
        message: `Failed to load custom views. ${error}`,
        intent: Intent.DANGER,
      });
    } finally {
      setLoadingCustomViews(false);
    }
  };

  const applyCustomView = async (viewName: string) => {
    if (hasCustomViews && viz) {
      try {
        await viz.workbook.showCustomViewAsync(viewName === 'Original' ? '' : viewName);

        // Update customViews state to reflect the selected view
        setCustomViews(prevCustomViews =>
          prevCustomViews.map(view => ({
            ...view,
            isSelected: view.name === viewName,
          }))
        );
      } catch (error) {
        AppToaster.show({
          message: `Failed to apply custom view. ${error}`,
          intent: Intent.DANGER,
        });
      }
    }
  };

  const saveCustomView = () => {
    setSaveCustomViewDialogOpen(true);
  };

  const handleSaveCustomView = async () => {
    if (!newCustomViewName.trim()) {
      AppToaster.show({
        message: 'Name cannot be empty',
        intent: Intent.WARNING,
      });
      return;
    }

    if (viz) {
      try {
        await viz.workbook.saveCustomViewAsync(newCustomViewName.trim());

        setSavedCustomViewName(newCustomViewName.trim());
        setNewCustomViewName('');
        setSaveCustomViewDialogOpen(false);
        setConfirmMessage('Do you want to set this as your default view?');
        setIsConfirmOpen(true);
      } catch (error) {
        AppToaster.show({
          message: 'Failed to save custom view.',
          intent: Intent.DANGER,
        });
      }
    }
  };

  const handleConfirmSetDefault = async () => {
    if (viz) {
      try {
        await viz.workbook.setActiveCustomViewAsDefaultAsync();
        AppToaster.show({
          message: `Custom view "${savedCustomViewName}" has been saved and set as the default view.`,
          intent: Intent.SUCCESS,
        });
      } catch (error) {
        AppToaster.show({
          message: 'Failed to set as default view.',
          intent: Intent.DANGER,
        });
      } finally {
        loadCustomViewList(viz);
        setIsConfirmOpen(false);
        setSavedCustomViewName('');
      }
    }
  };

  const handleCancelSetDefault = () => {
    AppToaster.show({
      message: `Custom view "${savedCustomViewName}" has been saved.`,
      intent: Intent.SUCCESS,
    });
    loadCustomViewList(viz);
    setIsConfirmOpen(false);
    setSavedCustomViewName('');
  };

  const refreshView = () => {
    if (viz) {
      viz.refreshDataAsync();
    }
  };

  const openExternalUrl = () => {
    if (viz) {
      window.open(viz.src as string, '_blank');
    }
  };

  const showDownloadDialog = (action: string) => {
    if (viz) {
      try {
        switch (action) {
          case 'Workbook':
            viz.displayDialogAsync(TableauDialogType.ExportWorkbook);
            break;
          case 'PNG':
            viz.exportImageAsync();
            break;
          case 'PDF':
            viz.displayDialogAsync(TableauDialogType.ExportPDF);
            break;
          case 'PowerPoint':
            viz.displayDialogAsync(TableauDialogType.ExportPowerPoint);
            break;
          case 'Data':
            viz.displayDialogAsync(TableauDialogType.ExportData);
            break;
          case 'Crosstab':
            viz.displayDialogAsync(TableauDialogType.ExportCrossTab);
            break;
          default:
            break;
        }
      } catch (error) {
        AppToaster.show({
          message: `Failed to download ${action}. ${error}`,
          intent: Intent.DANGER,
        });
      }
    }
  };

  const manageViews = () => {
    setShowManageViewsDialog(true);
  };

  const setAsDefault = async (viewName: string) => {
    if (viz) {
      try {
        await viz.workbook.showCustomViewAsync(viewName === 'Original' ? '' : viewName);
        await viz.workbook.setActiveCustomViewAsDefaultAsync();
        AppToaster.show({
          message: `Custom view "${viewName}" has been set as the default view.`,
          intent: Intent.SUCCESS,
        });
        loadCustomViewList(viz);
      } catch (error) {
        AppToaster.show({
          message: `Failed to set as default view. ${error}`,
          intent: Intent.DANGER,
        });
      }
    }
  };

  const deleteCustomView = (viewName: string) => {
    if (viz) {
      setDeleteConfirmMessage(`Are you sure you want to delete the custom view "${viewName}"?`);
      setDeleteViewName(viewName);
      setIsDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (viz) {
      try {
        await viz.workbook.removeCustomViewAsync(deleteViewName);
        AppToaster.show({
          message: `Custom view "${deleteViewName}" has been deleted.`,
          intent: Intent.SUCCESS,
        });
        await loadCustomViewList(viz);
      } catch (error) {
        AppToaster.show({
          message: 'Failed to delete custom view.',
          intent: Intent.DANGER,
        });
      } finally {
        setIsDeleteConfirmOpen(false);
        setDeleteViewName('');
      }
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteViewName('');
  };

  const downloadMenu = (
    <Menu>
      {['Workbook', 'PNG', 'PDF', 'PowerPoint', 'Data', 'Crosstab'].map((action, index) => (
        <MenuItem key={index} text={action} onClick={() => showDownloadDialog(action)}/>
      ))}
    </Menu>
  );

  const customViewsMenu = (
    <Menu>
      {loadingCustomViews ? (
        <MenuItem disabled={true} text="Loading..."/>
      ) : (
        customViews.map((view, index) => (
          <MenuItem
            key={index}
            text={`${view.name}${view.isDefault ? ' (default)' : ''}`}
            onClick={() => applyCustomView(view.name)}
            active={view.isSelected}
          />
        ))
      )}
    </Menu>
  );

  useEffect(() => {
    if (!loading && vizRef.current && embedUrl && embedUrl.includes('/views/')) {
      const newViz = new TableauViz();

      newViz.src = embedUrl;

      if (filters) {
        const filtersArray = filters.split('&').map(f => f.split('='));
        for (const [filterName, filterValue] of filtersArray) {
          newViz.addFilter(filterName, filterValue);
        }
      }

      onChange && onChange(embedUrl);

      // Hide the toolbar and tabs
      newViz.toolbar = 'hidden' as any;
      newViz.hideTabs = true;

      vizRef.current.innerHTML = '';
      vizRef.current.appendChild(newViz);

      setViz(newViz);
      setTableauIsAvailable(true);

      // Load custom views
      newViz.addEventListener(TableauEventType.FirstInteractive, () => {
        setViz(newViz);
        setTableauIsAvailable(true);
        loadCustomViewList(newViz);
      });
    }
  }, [loading, embedUrl, filters]);

  if (loading) {
    return <Loading className="view-preview-embed"/>;
  }

  return (
    <div className="tableau-embed-form__preview-embed" style={{width: '100%', overflow: 'hidden'}}>
      <div style={{marginBottom: 8}}>
        <Button
          small
          disabled={!tableauIsAvailable}
          onClick={openExternalUrl}
          icon={IconNames.SHARE}
          style={{marginRight: 5}}
        >
          Open in new tab
        </Button>

        <Popover2 content={customViewsMenu} position={Position.BOTTOM}>
          <Button
            small
            disabled={!tableauIsAvailable}
            icon={IconNames.EYE_OPEN}
            rightIcon={IconNames.CARET_DOWN}
            style={{marginRight: 5}}
          >
            View
          </Button>
        </Popover2>

        <Button
          small
          disabled={!tableauIsAvailable}
          icon={IconNames.REFRESH}
          onClick={refreshView}
          style={{marginRight: 5}}
        >
          Refresh
        </Button>

        <Button
          small
          disabled={!tableauIsAvailable}
          icon={IconNames.FLOPPY_DISK}
          onClick={saveCustomView}
          style={{marginRight: 5}}
        >
          Save Custom View
        </Button>

        <Button
          small
          disabled={!tableauIsAvailable}
          icon={IconNames.COG}
          onClick={manageViews}
          style={{marginRight: 5}}
        >
          Manage Views
        </Button>

        <Popover2 content={downloadMenu} position={Position.BOTTOM}>
          <Button
            small
            disabled={!tableauIsAvailable}
            icon={IconNames.DOWNLOAD}
            rightIcon={IconNames.CARET_DOWN}
            style={{marginRight: 5}}
          >
            Download
          </Button>
        </Popover2>
      </div>

      <div id="tableauViz" ref={vizRef} style={{width: '100%'}}></div>

      {/* Save Custom View Dialog */}
      <Dialog
        isOpen={saveCustomViewDialogOpen}
        onClose={() => {
          setSaveCustomViewDialogOpen(false);
          setNewCustomViewName('');
        }}
        title="Create Custom View"
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="Name" labelFor="custom-view-name-input">
            <InputGroup
              id="custom-view-name-input"
              placeholder="Enter name for your custom view"
              value={newCustomViewName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCustomViewName(e.target.value)}
            />
          </FormGroup>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setSaveCustomViewDialogOpen(false)}>Cancel</Button>
            <Button intent={Intent.PRIMARY} onClick={handleSaveCustomView}>
              Save
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Confirmation Alert for Set as Default */}
      <Alert
        isOpen={isConfirmOpen}
        cancelButtonText="No"
        confirmButtonText="Yes"
        intent={Intent.PRIMARY}
        onCancel={handleCancelSetDefault}
        onConfirm={handleConfirmSetDefault}
      >
        <p>{confirmMessage}</p>
      </Alert>

      {/* Confirmation Alert for Delete Custom View */}
      <Alert
        isOpen={isDeleteConfirmOpen}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        intent={Intent.DANGER}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      >
        <p>{deleteConfirmMessage}</p>
      </Alert>

      {/* Manage Views Dialog */}
      <Dialog
        isOpen={showManageViewsDialog}
        onClose={() => setShowManageViewsDialog(false)}
        title="Manage Custom Views"
      >
        <div className={Classes.DIALOG_BODY}>
          {customViews.length > 0 && customViews[0].name !== 'No saved views' ? (
            <table className={Classes.HTML_TABLE} style={{width: '100%'}}>
              <thead>
              <tr>
                <th>Name</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              {customViews.map((view, index) => (
                <tr key={index}>
                  <td>
                    {view.name} {view.isDefault ? '(default)' : ''}
                  </td>
                  <td>
                    {view.name !== 'Original' && (
                      <>
                        <Button small onClick={() => deleteCustomView(view.name)} style={{marginRight: 5}}>
                          Delete
                        </Button>
                        {!view.isDefault && (
                          <Button small onClick={() => setAsDefault(view.name)}>Set as default</Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          ) : (
            <p>No saved views</p>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowManageViewsDialog(false)}>Close</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default EmbedWidget;
